import React, { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import TagManager from 'react-gtm-module';

import { useAppDispatch } from 'app/hooks';
import {
  setFavorites,
  setLastStepDone,
  setSchoolShortlisted,
  UserState,
} from 'features/user/userSlice';

import { Program } from 'app/types';
import { useAppSelector } from 'app/hooks';
import Text from 'common/components/Text/Text';
import DetailInfo from 'common/components/DetailInfo/DetailInfo';
import { navigateTop, programLabelCard } from 'common/utils';
import {
  scheduleConversion,
  formatNumber,
  detailUrl,
  newDetailUrl,
} from 'common/utils/conversions';
import { useAddFavoriteMutation, useDeleteFavoriteMutation } from 'services/exploreApi';

import style from './ProgramCard.module.scss';
import defaultIcon from 'assets/explore/icon-default.svg';
import { ReactComponent as MoneyIcon } from 'assets/icons/money.svg';
import { SessionState } from 'features/session/sessionSlice';

interface CompProps {
  program: Program;
  selected?: string;
}

const ProgramCard = ({ program, selected }: CompProps): React.ReactElement => {
  const { logged } = useAppSelector(({ session }: { session: SessionState }) => session);
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [isFavorite, setFavorite] = useState<boolean>(program.favorite ? program.favorite : false);
  const isActive = program.id.toString() == selected;
  const { favorites } = useAppSelector(({ user }: { user: UserState }) => user);
  const url = detailUrl('programs', program.id, program.name);
  const newUrl = newDetailUrl('programs', program.id, program.name);
  const label = programLabelCard(program.preferences_score);
  const dispatch = useAppDispatch();
  const [addFavorite] = useAddFavoriteMutation();
  const [deleteFavorite] = useDeleteFavoriteMutation();

  const sendGTMEvent = (eventName: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'ga_event',
        eventName: eventName,
        programId: program.id,
        programName: program.name,
      },
    });
  };

  const handleAddFavorite = () => {
    const values = { id: program?.id, kind: 'Program' };
    if (logged) {
      addFavorite(values).then(async (resp: any) => {
        //setFavorite(!isFavorite);
        if (!favorites?.includes(program.id)) {
          dispatch(setFavorites([...(favorites || []), program.id]));
        }
        dispatch(setSchoolShortlisted(true));
        dispatch(setLastStepDone(5));
      });
      sendGTMEvent('program_shortlist');
    } else {
      setTimeout(() => {
        navigate('/signup');
      }, 100);
    }
  };
  const handleDeleteFavorite = () => {
    const values = { id: program?.id, kind: 'Program' };
    deleteFavorite(values).then(async (resp: any) => {
      //setFavorite(!isFavorite);
      await dispatch(setFavorites(favorites?.filter((id) => id !== program.id)));
    });
    sendGTMEvent('program_unshortlist');
  };

  useEffect(() => {
    if (favorites?.includes(program ? program.id : -1)) {
      setFavorite(true);
    } else {
      setFavorite(false);
    }
  }, [favorites]);

  return (
    <div
      className={`card ${style.programCard} ${isActive && !isMobile ? style.active : ''}`}
      key={program.id}
      onClick={() => (isMobile ? null : (navigate(url), navigateTop(0, 0, 'detailContainer')))}
    >
      {isMobile && label && (
        <div className={style.labelContainer} style={{ backgroundColor: label.background }}>
          <div className={style.leftArrow}></div>
          <div className={style.label} style={{ color: label.text }}>
            {label.title}
          </div>
          <div className={style.rightArrow}></div>
        </div>
      )}
      <div className={style.header}>
        <div className={style.schoolInfo}>
          <img className={style.avatar} src={program.logo || defaultIcon} />
          <div className={style.info}>
            <div className={style.name}>{program.name}</div>
            <div className={style.school}>{program.school_name}</div>
          </div>
        </div>
        {isFavorite ? (
          <div
            className={style.heart_fill}
            onClick={() => {
              handleDeleteFavorite();
            }}
          >
            <i className="bi bi-heart-fill"></i>
          </div>
        ) : isMobile ? (
          <div
            className={style.black_heart}
            onClick={() => {
              handleAddFavorite();
            }}
          >
            <i className="bi bi-heart"></i>
          </div>
        ) : (
          <div
            className={style.grey_heart}
            onClick={() => {
              handleAddFavorite();
            }}
          >
            <i className="bi bi-heart"></i>
          </div>
        )}
      </div>
      <div className={style.body}>
        {isMobile && (
          <div className={style.details}>
            <DetailInfo
              title={
                program.multiple_locations ? 'Multiple' : program.study_options_first.city.name
              }
              subtitle={
                program.multiple_locations
                  ? program.online_delivery
                    ? 'and Online'
                    : ''
                  : program.study_options_first.city.country.name
              }
              icon={<i className="bi bi-geo-alt"></i>}
              className={style.detailItem}
            />
            <DetailInfo
              title={`${program.study_options_first.duration_in_months} months`}
              subtitle={scheduleConversion(program.study_options_first.schedule)}
              icon={<i className="bi bi-clock"></i>}
              className={style.detailItem}
              many={program.multiple_durations ? true : false}
            />
            {program.study_options_first?.tuition_fee &&
              program.study_options_first.tuition_fee.amount_international !== null &&
              (program.study_options_first.tuition_fee.amount_international > 0 ? (
                <DetailInfo
                  title={
                    program.many_study_options
                      ? formatNumber(program.tuition_min)
                      : formatNumber(program.study_options_first.tuition_fee?.amount_international)
                  }
                  subtitle={`${program.study_options_first.tuition_fee.currency}`}
                  description="Tuition fee"
                  icon={<MoneyIcon />}
                  className={style.detailItem}
                  many={program.many_study_options ? true : false}
                />
              ) : (
                <DetailInfo
                  title={0}
                  description="Tuition fee"
                  icon={<MoneyIcon />}
                  className={style.detailItem}
                  many={program.many_study_options ? true : false}
                />
              ))}
          </div>
        )}

        <div className={style.tagContainer}>
          {program.tags.map((tag) => (
            <div className={style.tag} key={tag.id}>
              {tag.name}
            </div>
          ))}
        </div>

        {isMobile ? (
          <div className={style.description}>
            <Text expandable maxHeight="36px" color="secondary" size={13}>
              {program.description}
            </Text>
          </div>
        ) : (
          <>
            <div className={style.descriptionDesktop}>
              {program.study_options_first?.tuition_fee &&
                program.study_options_first.tuition_fee.amount_international !== null &&
                (program.study_options_first.tuition_fee.amount_international > 0 ? (
                  <>
                    {program.many_study_options ? (
                      <>
                        <Text size={14} inline>
                          From{' '}
                          <Text size={14} weight={700} inline>
                            {`${program.tuition_min_currency} `}
                          </Text>
                          <Text size={14} weight={700} inline>
                            {formatNumber(program.tuition_min)}
                          </Text>
                        </Text>
                      </>
                    ) : (
                      <Text size={14} weight={700} inline>
                        {`${program.study_options_first.tuition_fee.currency} ${formatNumber(
                          program.study_options_first.tuition_fee.amount_international
                        )}`}
                      </Text>
                    )}
                  </>
                ) : (
                  <Text size={14} weight={700} inline>
                    0 tuition fee
                  </Text>
                ))}
              <Text
                size={14}
                weight={400}
                inlineStyle={{ lineHeight: '24px', marginLeft: '5px' }}
                inline
              >
                {`${
                  program.multiple_schedules
                    ? 'full-time/part-time'
                    : scheduleConversion(program.study_options_first.schedule, true)
                }, ${program.multiple_durations ? 'from' : ''} ${
                  program.study_options_first.duration_in_months
                } months`}
              </Text>
            </div>
            <div className={style.locationDesktop}>
              <i className="bi bi-geo-alt"></i>
              <Text size={14} inline>
                {program.multiple_locations
                  ? program.online_delivery
                    ? 'Multiple and Online'
                    : 'Multiple'
                  : `${program.study_options_first.city.name}, ${program.study_options_first.city.country.name}`}
              </Text>
              {!isMobile && label && (
                <div className={style.labelContainer} style={{ backgroundColor: label.background }}>
                  <div className={style.leftArrow}></div>
                  <div className={style.label} style={{ color: label.text }}>
                    {label.title}
                  </div>
                  <div className={style.rightArrow}></div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {isMobile && (
        <div className={style.actions}>
          <button
            className="btn btn-primary"
            onClick={() => (isMobile ? navigate(newUrl) : (navigate(url), navigateTop()))}
          >
            See details
          </button>
        </div>
      )}
    </div>
  );
};

export default ProgramCard;
